import React, { useState, useEffect, useMemo, useCallback } from "react";
import { TablePagination, usePagination } from "@/components/TablePagination";
import { InventoryItem, DocumentRecord } from "@/hooks/useDocuments";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "@/components/ui/table";

import { TableToolbar, exportToCSV, exportToExcel } from "@/components/TableToolbar";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, Check, X, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { EditableCell } from "@/components/ui/EditableCell";
import { supabase } from "@/integrations/supabase/client";
import { useAuditTrail } from "@/hooks/useAuditTrail";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { ResizableTableHead } from "@/components/ResizableTableHead";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { InventoryCardMobile } from "@/components/InventoryCardMobile";
import { calcSubTotal, calcHeaderTotals } from "@/hooks/useInvoiceMath";
import { calcShipmentLandedCost } from "@/hooks/useShipmentLandedCost";
import { useTRM } from "@/hooks/useTRM";
import { formatHsCode, useComplianceLookup } from "@/hooks/useComplianceLookup";
import { isPackingType, isTransportType } from "@/lib/documentTypes";
import { useEmbarqueLockStatus } from "@/hooks/useEmbarqueLockStatus";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { ShieldCheck, AlertCircle, Loader2 } from "lucide-react";
import { TruncatedCell } from "@/components/TruncatedCell";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ComplianceModal } from "@/components/ComplianceModal";
import { HsCodeSuggestion } from "@/components/HsCodeSuggestion";
import { BenefitAduaneroBadge, BenefitAduaneroSelect, resolveBenefitValue } from "@/components/BenefitAduaneroSelect";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Props {
  items: InventoryItem[];
  loading?: boolean;
  linkedDocumentId?: string | null;
  documentType?: string;
  /** When true, audit mode is always on and the switch is hidden */
  forceAuditMode?: boolean;
  /** Pre-computed landed costs from shipment-level engine (keyed by item.id) */
  externalLandedCosts?: Record<string, {
    landedCostUnitario: number;
    landedCostUnitarioCOP: number | null;
    fobItemCOP?: number;
    fleteAsignado?: number;
    seguroAsignado?: number;
    otrosAsignados?: number;
    arancelItemCOP?: number;
    ivaItemCOP?: number;
    arancelPctEfectivo?: number;
    ivaPctEfectivo?: number;
    landedCostTotalCOP?: number;
    monedaOrigen?: string;
    /** ICO — Impuesto al Consumo total del ítem en COP. */
    icoCOP?: number;
  }>;
  /** TRM aduanera del embarque — usada en el tooltip para mostrar la tasa real aplicada por el motor. */
  externalTrmAduana?: number | null;
  /** Barrera 3 — abre el recibo de trazabilidad para el ítem seleccionado. */
  onOpenTraceability?: (itemId: string) => void;
}

const EDITABLE_FIELDS = [
  "no_documento", "item_no", "sku", "hs_code", "descripcion",
  "cantidad", "unidad_medida", "precio_unitario", "sub_total", "nota_item", "peso_bruto_kg", "iva_override",
  "grados_alcoholicos", "litros_por_unidad", "tarifa_ico",
] as const;

type EditableField = typeof EDITABLE_FIELDS[number];

export function InventoryTable({ items, loading, linkedDocumentId, documentType, forceAuditMode, externalLandedCosts, externalTrmAduana, onOpenTraceability }: Props) {
  const isPacking = isPackingType(documentType);
  const isTransport = isTransportType(documentType);
  const isFinancial = !isPacking && !isTransport;
  const isMobile = useIsMobile();
  const { trmMap, trm, trmLoading } = useTRM();
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [complianceModal, setComplianceModal] = useState<{ item: InventoryItem } | null>(null);
  const [auditMode, setAuditMode] = useState(false);
  const showAuditCols = isFinancial && (forceAuditMode || auditMode);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { logChange } = useAuditTrail();
  const { currentPage, rowsPerPage, setCurrentPage, handleRowsPerPageChange, resetPage, paginate } = usePagination(20);


  // 🔒 Lock-down: si el embarque está liquidado, toda edición/eliminación queda bloqueada.
  const { isLocked: isEmbarqueLocked } = useEmbarqueLockStatus({ documentId: linkedDocumentId });

  // ── Inline cell editing via EditableCell ──
  const INLINE_EDITABLE: EditableField[] = ["cantidad", "precio_unitario", "peso_bruto_kg"];

  const handleInlineSave = useCallback(async (item: InventoryItem, field: EditableField, rawValue: string) => {
    if (isEmbarqueLocked) {
      toast({
        title: "Embarque liquidado",
        description: "Los costos están congelados. No se permiten ediciones inline.",
        variant: "destructive",
      });
      return;
    }
    const CONGELADO_OLD_VALUE = String((item as any)[field] ?? "");

    const isNumericField = ["cantidad", "precio_unitario", "peso_bruto_kg", "iva_override", "grados_alcoholicos", "litros_por_unidad", "tarifa_ico"].includes(field);
    let dbValue: any;
    if (isNumericField) {
      dbValue = parseFloat(rawValue.replace(/[^0-9.\-]/g, "")) || 0;
    } else {
      dbValue = rawValue.trim() || null;
    }

    // ── Optimistic update ──
    const optimisticUpdate: Record<string, any> = { [field]: dbValue };
    if (field === "cantidad" || field === "precio_unitario") {
      const cant = field === "cantidad" ? dbValue : Number(item.cantidad) || 0;
      const precio = field === "precio_unitario" ? dbValue : Number(item.precio_unitario) || 0;
      optimisticUpdate.sub_total = cant * precio;
    }

    await queryClient.cancelQueries({ queryKey: ["inventory"] });
    const inventoryQueries = queryClient.getQueriesData<InventoryItem[]>({ queryKey: ["inventory"] });
    const snapshots: [readonly unknown[], InventoryItem[] | undefined][] = [];
    for (const [key, data] of inventoryQueries) {
      snapshots.push([key, data]);
      if (data) {
        queryClient.setQueryData(key, data.map(i =>
          i.id === item.id ? { ...i, ...optimisticUpdate } : i
        ));
      }
    }

    toast({ title: "✅ Celda actualizada" });

    // ── Use atomic RPC for numeric fields, standard update for text ──
    let error: any = null;
    if (isNumericField) {
      const { error: rpcError } = await supabase.rpc("update_inventory_item_atomic", {
        p_item_id: item.id,
        p_field: field,
        p_value: dbValue,
      });
      error = rpcError;
    } else {
      const updatePayload: Record<string, any> = { [field]: dbValue };

      // HITL: si el usuario aprobó o editó el HS Code, transferimos la responsabilidad al humano
      if (field === "hs_code") {
        updatePayload.hs_code_deducido_por_ia = false;
      }

      const { error: updateError } = await supabase
        .from("base_inventario")
        .update(updatePayload)
        .eq("id", item.id);
      error = updateError;
    }

    if (error) {
      console.error(`Save failed for item ${item.id}, field ${field}:`, error);
      toast({ title: "Error al guardar", description: error.message, variant: "destructive" });
      for (const [key, data] of snapshots) {
        queryClient.setQueryData(key, data);
      }
      return;
    }

    // Audit trail after successful DB write
    await logChange(item.documento_id, field, CONGELADO_OLD_VALUE, String(dbValue ?? ""));

    if (["cantidad", "precio_unitario", "peso_bruto_kg"].includes(field)) {
      const docId = item.documento_id;
      const currentItems = queryClient.getQueryData<InventoryItem[]>(
        inventoryQueries[0]?.[0] ?? ["inventory"]
      ) ?? items;
      const updatedSiblings = currentItems
        .filter(i => i.documento_id === docId)
        .map(i => i.id === item.id ? { ...i, ...optimisticUpdate } : i);
      const allCaches = queryClient.getQueriesData<DocumentRecord[]>({ queryKey: ["documents"] });
      let pDoc: DocumentRecord | undefined;
      for (const [, data] of allCaches) { pDoc = data?.find(d => d.id === docId); if (pDoc) break; }
      const flete = parseFloat(String(pDoc?.costo_flete)) || 0;
      const seg = parseFloat(String(pDoc?.seguro)) || 0;
      const imp = parseFloat(String(pDoc?.impuestos)) || 0;
      const desc = parseFloat(String(pDoc?.descuento_ajuste)) || 0;
      const totals = calcHeaderTotals(updatedSiblings, flete, imp, desc, seg);
      await supabase.from("registro_documentos").update({
        total_piezas: totals.total_piezas,
        peso_bruto_kg: totals.peso_bruto_kg,
        total_factura: totals.total_factura,
      }).eq("id", docId);
    }

    // Only invalidate AFTER successful DB write
    queryClient.invalidateQueries({ queryKey: ["inventory"] });
    queryClient.invalidateQueries({ queryKey: ["documents"] });
    queryClient.invalidateQueries({ queryKey: ["all-documents"] });
    queryClient.invalidateQueries({ queryKey: ["embarque-documents"] });
  }, [items, queryClient, toast, isEmbarqueLocked]);

  const renderInlineCell = (item: InventoryItem, field: EditableField, display: React.ReactNode) => {
    if (editingId === item.id) return renderCell(item, field, display);
    if (!INLINE_EDITABLE.includes(field)) return display;
    return (
      <EditableCell
        value={(item as any)[field]}
        type={["cantidad", "precio_unitario", "peso_bruto_kg"].includes(field) ? "number" : "text"}
        displayFormat={() => display}
        onSave={async (val) => handleInlineSave(item, field, val)}
        disabled={!!editingId || isEmbarqueLocked}
      />
    );
  };

  // B-04: Use the key-based API (resultsByKey + statusByKey) keyed by item.id
  // to eliminate positional indexing bugs (R1) and surface unknown HS codes (R3).
  const {
    resultsByKey: complianceByKey,
    statusByKey: complianceStatusByKey,
    lookupByKey: complianceLookupByKey,
  } = useComplianceLookup();

  // Build a map from item id to compliance result (gravamen/iva extracted).
  const complianceByItemId = useMemo(() => {
    const map: Record<string, { gravamen: number; iva: number }> = {};
    items.forEach((item) => {
      const cr = complianceByKey[item.id];
      if (cr) {
        map[item.id] = {
          gravamen: cr.gravamen_porcentaje,
          iva: cr.iva_porcentaje,
        };
      }
    });
    return map;
  }, [complianceByKey, items]);

  // Get parent document for flete/seguro (reactive via query cache)
  const parentDoc = useMemo(() => {
    if (!items.length) return undefined;
    const docId = items[0].documento_id;
    const allCaches = queryClient.getQueriesData<DocumentRecord[]>({ queryKey: ["documents"] });
    for (const [, data] of allCaches) {
      const found = data?.find(d => d.id === docId);
      if (found) return found;
    }
    return undefined;
  }, [items, queryClient]);

  // Landed cost calculation — use external (shipment-level) if provided, else
  // run the v2 engine inline for the parent document (penny-perfect, IVA-aware).
  const landedCosts = useMemo(() => {
    if (externalLandedCosts) {
      // Map items to legacy shape from external (shipment-level pre-computed).
      return items.map(item => {
        const ext = externalLandedCosts[item.id];
        const lcUnitCOP = ext?.landedCostUnitarioCOP ?? null;
        // Derive USD-equivalent unit cost for "Costo Destino (Und)" column display.
        const lcUnitUSD = lcUnitCOP != null && trm && trm > 0 ? lcUnitCOP / trm : 0;
        return {
          landedCostUnitario: lcUnitUSD,
          landedCostUnitarioCOP: lcUnitCOP,
        };
      });
    }
    if (!parentDoc || !items.length) {
      return items.map(() => ({ landedCostUnitario: 0, landedCostUnitarioCOP: null as number | null }));
    }
    // Per-document v2: feed the canonical engine with this single doc + its items.
    const arancelByItemId: Record<string, number> = {};
    const ivaByItemId: Record<string, number> = {};
    items.forEach((item) => {
      const c = complianceByItemId[item.id];
      if (c) {
        const benefit = resolveBenefitValue((item as any).beneficio_aduanero, item.aplica_tlc);
        // Matriz: GENERAL → lookup; TLC_PURO → arancel 0; LEY_1715/ZONA_FRANCA → arancel + iva 0.
        const arancelPct = benefit === "GENERAL" ? c.gravamen : 0;
        const ivaPct = benefit === "LEY_1715" || benefit === "ZONA_FRANCA" ? 0 : c.iva;
        arancelByItemId[item.id] = arancelPct;
        ivaByItemId[item.id] = ivaPct;
      }
    });
    const result = calcShipmentLandedCost(
      [parentDoc],
      items,
      trmMap,
      arancelByItemId,
      [], // no gastos_embarque at single-doc scope
      null, // no trm_aduana available at this view
      ivaByItemId,
    );
    const byId = new Map(result.items.map((r) => [r.itemId, r]));
    return items.map((item) => {
      const r = byId.get(item.id);
      const lcUnitCOP = r?.landedCostUnitarioCOP ?? null;
      const lcUnitUSD = lcUnitCOP != null && trm && trm > 0 ? lcUnitCOP / trm : 0;
      return { landedCostUnitario: lcUnitUSD, landedCostUnitarioCOP: lcUnitCOP };
    });
  }, [items, parentDoc, complianceByItemId, trm, trmMap, externalLandedCosts]);

  // Reactive compliance lookup — only for non-packing documents.
  // B-04: Keyed by item.id so re-orders / filters don't shuffle results.
  const hsCodesKey = items.map(i => `${i.id}:${i.hs_code ?? ""}`).join("|");
  useEffect(() => {
    if (!items.length || isPacking) return;
    items.forEach((item) => {
      const code = (item.hs_code ?? "").trim();
      if (code) complianceLookupByKey(item.id, code);
    });
  }, [hsCodesKey, complianceLookupByKey, isPacking]);

  // Sprint 3.2: Auto-update REMOVED — HITL pattern enforced.
  // The compliance hook is read-only; hs_code changes require explicit user action.

  const filtered = items.filter(item =>
    item.no_documento.toLowerCase().includes(search.toLowerCase()) ||
    (item.descripcion || "").toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => { resetPage(); }, [search]);

  const paginatedItems = useMemo(() => paginate(filtered), [filtered, currentPage, rowsPerPage]);

  const getExportData = () => {
    const headers = ["No Documento", "Item", "SKU", "HS Code", "Descripción", "Cantidad", "Unidad"];
    const numericCols = [5]; // Cantidad
    if (isPacking) {
      headers.push("Peso (KG)");
      numericCols.push(7);
    } else if (externalLandedCosts) {
      // Shipment view: expand full proration breakdown
      headers.push("P. Unitario", "Subtotal", "Arancel %", "IVA %",
        "FOB Item (COP)", "Flete Asignado (COP)", "Seguro Asignado (COP)",
        "Otros Cargos (COP)", "Arancel / Impuestos (COP)",
        "Landed Cost Unitario (COP)", "Landed Cost Total (COP)");
      numericCols.push(7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17);
    } else {
      headers.push("P. Unitario", "Subtotal", "Arancel %", "IVA %", "Costo Destino (Und)", "Costo COP (Und)");
      numericCols.push(7, 8, 9, 10, 11, 12);
    }
    headers.push("Nota");
    const rows = filtered.map(i => {
      const row: (string | number)[] = [i.no_documento, i.item_no, i.sku || "", i.hs_code || "", i.descripcion || "", Number(i.cantidad), i.unidad_medida || ""];
      if (isPacking) {
        row.push(Number(i.peso_bruto_kg || 0));
      } else if (externalLandedCosts) {
        const ext = externalLandedCosts[i.id];
        const qty = Number(i.cantidad) || 0;
        const lcUnitCOP = ext?.landedCostUnitarioCOP ?? 0;
        row.push(
          Number(i.precio_unitario), Number(i.sub_total),
          complianceByItemId[i.id]?.gravamen ?? "", complianceByItemId[i.id]?.iva ?? "",
          ext?.fobItemCOP ?? 0, ext?.fleteAsignado ?? 0, ext?.seguroAsignado ?? 0,
          ext?.otrosAsignados ?? 0, ext?.arancelItemCOP ?? 0,
          lcUnitCOP, lcUnitCOP * qty
        );
      } else {
        const itemIdx = items.indexOf(i);
        const lc = landedCosts[itemIdx];
        row.push(Number(i.precio_unitario), Number(i.sub_total), complianceByItemId[i.id]?.gravamen ?? "", complianceByItemId[i.id]?.iva ?? "", lc ? lc.landedCostUnitario : 0, lc?.landedCostUnitarioCOP ?? "");
      }
      row.push(i.nota_item || "");
      return row;
    });
    return { headers, rows, numericCols };
  };

  const handleExportCSV = () => {
    const { headers, rows } = getExportData();
    exportToCSV(headers, rows.map(r => r.map(String)), "inventario.csv");
  };

  const handleExportExcel = async () => {
    try {
      const { headers, rows, numericCols } = getExportData();
      await exportToExcel(headers, rows, `kargonode_inventario_${new Date().getTime()}.xlsx`, numericCols);
    } catch (err) {
      console.error("Error exportando Excel:", err);
      toast({ title: "Error al exportar", description: "No se pudo generar el archivo Excel.", variant: "destructive" });
    }
  };

  const startEdit = (item: InventoryItem) => {
    setEditingId(item.id);
    const values: Record<string, string> = {};
    for (const f of EDITABLE_FIELDS) {
      values[f] = String((item as any)[f] ?? "");
    }
    // Pre-populate iva_override from compliance if not already set
    if (!values["iva_override"] || values["iva_override"] === "null" || values["iva_override"] === "") {
      if (complianceByItemId[item.id]) {
        values["iva_override"] = String(complianceByItemId[item.id].iva);
      }
    }
    setEditValues(values);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValues({});
  };

  const saveEdit = async () => {
    if (!editingId) return;
    const editedItem = items.find(i => i.id === editingId);
    if (!editedItem) { cancelEdit(); return; }

    // ── Freeze old values BEFORE building update ──
    const CONGELADO_OLD: Record<string, string> = {};
    for (const f of EDITABLE_FIELDS) {
      CONGELADO_OLD[f] = String((editedItem as any)[f] ?? "");
    }

    const update: Record<string, any> = {};
    for (const f of EDITABLE_FIELDS) {
      if (["cantidad", "precio_unitario", "sub_total", "peso_bruto_kg"].includes(f)) {
        update[f] = parseFloat(editValues[f]) || 0;
      } else if (f === "item_no") {
        update[f] = parseInt(editValues[f]) || 0;
      } else if (f === "iva_override") {
        const val = parseFloat(editValues[f]);
        update[f] = isNaN(val) ? null : val;
      } else {
        update[f] = editValues[f] || null;
      }
    }

    const docId = editedItem.documento_id;

    // Recalculate parent doc totals from sibling items
    const updatedSiblings = items
      .filter(i => i.documento_id === docId)
      .map(i => i.id === editingId
        ? { ...i, cantidad: update.cantidad, precio_unitario: update.precio_unitario, sub_total: update.sub_total, peso_bruto_kg: update.peso_bruto_kg }
        : i
      );

    // Find parent doc from any matching cache (use prefix matching)
    const allCaches = queryClient.getQueriesData<DocumentRecord[]>({ queryKey: ["documents"] });
    let parentDoc: DocumentRecord | undefined;
    for (const [, data] of allCaches) {
      parentDoc = data?.find(d => d.id === docId);
      if (parentDoc) break;
    }
    const flete = parseFloat(String(parentDoc?.costo_flete)) || 0;
    const seg = parseFloat(String(parentDoc?.seguro)) || 0;
    const imp = parseFloat(String(parentDoc?.impuestos)) || 0;
    const desc = parseFloat(String(parentDoc?.descuento_ajuste)) || 0;
    const totals = calcHeaderTotals(updatedSiblings, flete, imp, desc, seg);

    // Close edit mode immediately
    cancelEdit();
    toast({ title: "✅ Ítem actualizado" });

    // Persist item to DB
    const { error } = await supabase.from("base_inventario").update(update).eq("id", editingId);
    if (error) {
      toast({ title: "Error al guardar ítem", description: error.message, variant: "destructive" });
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      return;
    }

    // ── Audit trail: log each changed field ──
    for (const f of EDITABLE_FIELDS) {
      const oldVal = CONGELADO_OLD[f].trim();
      const newVal = String(update[f] ?? "").trim();
      if (oldVal !== newVal) {
        await logChange(docId, f, oldVal, newVal);
      }
    }

    // Persist parent doc totals
    await supabase.from("registro_documentos").update({
      total_piezas: totals.total_piezas,
      peso_bruto_kg: totals.peso_bruto_kg,
      total_factura: totals.total_factura,
    }).eq("id", docId);

    // Invalidate all matching queries so UI refreshes with DB truth
    queryClient.invalidateQueries({ queryKey: ["inventory"] });
    queryClient.invalidateQueries({ queryKey: ["documents"] });
    queryClient.invalidateQueries({ queryKey: ["all-documents"] });
    queryClient.invalidateQueries({ queryKey: ["embarque-documents"] });
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    const { error } = await supabase.from("base_inventario").delete().eq("id", deleteId);

    if (error) {
      toast({ title: "Error al eliminar", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "🗑️ Ítem eliminado" });
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      queryClient.invalidateQueries({ queryKey: ["embarque-documents"] });
    }
    setDeleteId(null);
  };

  const renderCell = (item: InventoryItem, field: EditableField, display?: React.ReactNode) => {
    if (editingId === item.id) {
      const isSubTotalField = field === "sub_total";
      return (
        <Input
          value={editValues[field] ?? ""}
          readOnly={isSubTotalField}
          onChange={(e) => {
            const newVals = { ...editValues, [field]: e.target.value };
            // Cascading: auto-calc sub_total when cantidad or precio_unitario change
            if (field === "cantidad" || field === "precio_unitario") {
              const cant = field === "cantidad" ? e.target.value : newVals["cantidad"];
              const precio = field === "precio_unitario" ? e.target.value : newVals["precio_unitario"];
              newVals["sub_total"] = String(calcSubTotal(cant, precio));
            }
            setEditValues(newVals);
          }}
          className={cn("h-7 text-xs min-w-[80px]", isSubTotalField && "bg-muted/50")}
        />
      );
    }
    return display ?? (item[field] as string) ?? "";
  };

  const copFmt = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const thClass = "text-xs text-muted-foreground font-semibold";

  return (
    <div className="rounded-xl overflow-hidden bg-card border border-border shadow-sm">
      <div className="p-4 border-b border-border">
        <h3 className="text-sm font-semibold text-foreground">
          Base Inventario
          {linkedDocumentId && <span className="ml-2 text-xs font-normal text-primary">(filtrado por documento seleccionado)</span>}
        </h3>
        <p className="text-xs text-muted-foreground mt-0.5">{loading ? "Cargando..." : `${filtered.length} items`}</p>
      </div>
      <div className={cn("flex items-center justify-between gap-4 px-4", isMobile && "flex-col items-stretch gap-2")}>
        <div className="flex-1">
          <TableToolbar searchValue={search} onSearchChange={setSearch} onExportCSV={handleExportCSV} onExportExcel={handleExportExcel} placeholder="Buscar por No. Documento o Descripción..." />
        </div>
        {isFinancial && !forceAuditMode && (
          <div className={cn("flex items-center gap-2 shrink-0", isMobile && "bg-muted/50 rounded-lg px-3 py-2.5")}>
            <Switch id="audit-mode" checked={auditMode} onCheckedChange={setAuditMode} />
            <Label htmlFor="audit-mode" className={cn("text-xs text-muted-foreground cursor-pointer whitespace-nowrap", isMobile && "text-sm")}>
              Modo Auditoría Aduanera
            </Label>
          </div>
        )}
        {showAuditCols && (
          <Badge variant="outline" className="shrink-0 text-xs font-mono">
            {trmLoading ? "TRM cargando…" : trm ? `TRM USD: $${trm.toLocaleString("es-CO")} COP` : "TRM no disponible"}
          </Badge>
        )}
      </div>
      {isMobile ? (
        <div className="p-3 space-y-3">
          {filtered.map((item) => {
            const itemIdx = items.indexOf(item);
            const cr = complianceByKey[item.id];
            const lc = landedCosts[itemIdx];
            return (
            <InventoryCardMobile
              key={item.id}
              item={item}
              isPacking={isPacking}
              showAudit={showAuditCols}
              complianceResult={cr}
              landedCostUnitario={lc?.landedCostUnitario}
              onOpenCompliance={() => setComplianceModal({ item })}
              onEdit={startEdit}
              onDelete={(id) => setDeleteId(id)}
              onSaveItem={async (id, update) => {
                const editedItem = items.find(i => i.id === id);
                // ── Freeze old values ──
                const CONGELADO_OLD: Record<string, string> = {};
                if (editedItem) {
                  for (const key of Object.keys(update)) {
                    CONGELADO_OLD[key] = String((editedItem as any)[key] ?? "");
                  }
                }
                // Persist item
                const { error } = await supabase.from("base_inventario").update(update).eq("id", id);
                if (error) {
                  toast({ title: "Error al guardar", description: error.message, variant: "destructive" });
                  return false;
                }
                // ── Audit trail ──
                if (editedItem) {
                  for (const key of Object.keys(update)) {
                    const oldVal = (CONGELADO_OLD[key] ?? "").trim();
                    const newVal = String(update[key] ?? "").trim();
                    if (oldVal !== newVal) {
                      await logChange(editedItem.documento_id, key, oldVal, newVal);
                    }
                  }
                }
                // Recalc parent totals
                if (editedItem) {
                  const docId = editedItem.documento_id;
                  const updatedSiblings = items
                    .filter(i => i.documento_id === docId)
                    .map(i => i.id === id ? { ...i, ...update } : i);
                  const allCaches = queryClient.getQueriesData<DocumentRecord[]>({ queryKey: ["documents"] });
                  let parentDoc: DocumentRecord | undefined;
                  for (const [, data] of allCaches) {
                    parentDoc = data?.find(d => d.id === docId);
                    if (parentDoc) break;
                  }
                   const flete = parseFloat(String(parentDoc?.costo_flete)) || 0;
                   const seg = parseFloat(String(parentDoc?.seguro)) || 0;
                   const imp = parseFloat(String(parentDoc?.impuestos)) || 0;
                   const desc = parseFloat(String(parentDoc?.descuento_ajuste)) || 0;
                   const totals = calcHeaderTotals(updatedSiblings, flete, imp, desc, seg);
                  await supabase.from("registro_documentos").update({
                    total_piezas: totals.total_piezas,
                    peso_bruto_kg: totals.peso_bruto_kg,
                    total_factura: totals.total_factura,
                  }).eq("id", docId);
                }
                toast({ title: "✅ Ítem actualizado" });
                queryClient.invalidateQueries({ queryKey: ["inventory"] });
                queryClient.invalidateQueries({ queryKey: ["documents"] });
                queryClient.invalidateQueries({ queryKey: ["all-documents"] });
                queryClient.invalidateQueries({ queryKey: ["embarque-documents"] });
                return true;
              }}
            />
            );
          })}
          {filtered.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-8">No se encontraron ítems</p>
          )}
        </div>
      ) : (
        <div
          className="w-full overflow-x-auto rounded-md border pb-2"
          onWheel={(e) => {
            if (e.shiftKey) {
              e.preventDefault();
              e.stopPropagation();
              e.currentTarget.scrollLeft += e.deltaY;
            }
          }}
        >
          <div className="min-w-[1450px]">
            <Table style={{ tableLayout: "fixed" }}>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <ResizableTableHead className={thClass} initialWidth={110} minWidth={70}>No Documento</ResizableTableHead>
                  <ResizableTableHead className={thClass} initialWidth={60} minWidth={40}>Item</ResizableTableHead>
                  <ResizableTableHead className={thClass} initialWidth={90} minWidth={60}>SKU</ResizableTableHead>
                  {showAuditCols && <ResizableTableHead className={thClass} initialWidth={110} minWidth={70}>HS Code</ResizableTableHead>}
                  <ResizableTableHead className={thClass} initialWidth={150} minWidth={80}>Descripción</ResizableTableHead>
                  <ResizableTableHead className={cn(thClass, "text-right")} initialWidth={80} minWidth={50}>Cantidad</ResizableTableHead>
                  <ResizableTableHead className={thClass} initialWidth={70} minWidth={50}>Unidad</ResizableTableHead>
                  {(isPacking || isTransport) && <ResizableTableHead className={cn(thClass, "text-right")} initialWidth={100} minWidth={60}>Peso (KG)</ResizableTableHead>}
                  {isFinancial && <ResizableTableHead className={cn(thClass, "text-right")} initialWidth={100} minWidth={60}>P. Unitario</ResizableTableHead>}
                  {isFinancial && <ResizableTableHead className={cn(thClass, "text-right")} initialWidth={100} minWidth={60}>Subtotal</ResizableTableHead>}
                  {showAuditCols && <ResizableTableHead className={cn(thClass, "text-right")} initialWidth={140} minWidth={120}>Arancel %</ResizableTableHead>}
                  {showAuditCols && <ResizableTableHead className={cn(thClass, "text-right")} initialWidth={70} minWidth={50}>IVA %</ResizableTableHead>}
                  {showAuditCols && !externalLandedCosts && <ResizableTableHead className={cn(thClass, "text-right")} initialWidth={120} minWidth={80}>Costo Destino (Und)</ResizableTableHead>}
                  {showAuditCols && <ResizableTableHead className={cn(thClass, "text-right")} initialWidth={130} minWidth={90}>{externalLandedCosts ? "Landed Cost Unitario (COP)" : "Costo COP (Und)"}</ResizableTableHead>}
                  {showAuditCols && externalLandedCosts && <ResizableTableHead className={cn(thClass, "text-right")} initialWidth={140} minWidth={100}>Landed Cost Total (COP)</ResizableTableHead>}
                  {showAuditCols && <ResizableTableHead className={cn(thClass, "text-right")} initialWidth={110} minWidth={80}>ICO (COP)</ResizableTableHead>}
                  {showAuditCols && <ResizableTableHead className={cn(thClass, "text-center")} initialWidth={100} minWidth={60}>Cumplimiento</ResizableTableHead>}
                  {showAuditCols && <ResizableTableHead className={cn(thClass, "text-right")} initialWidth={120} minWidth={80}>Precio Venta (COP)</ResizableTableHead>}
                  {showAuditCols && <ResizableTableHead className={cn(thClass, "text-center")} initialWidth={100} minWidth={70}>Margen Real</ResizableTableHead>}
                  <ResizableTableHead className={thClass} initialWidth={120} minWidth={70}>Nota</ResizableTableHead>
                  <ResizableTableHead className={cn(thClass, "text-center")} initialWidth={100} minWidth={70}>Acciones</ResizableTableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedItems.map((item) => (
                  <React.Fragment key={item.id}>
                  <TableRow className="border-border hover:bg-muted/30 transition-colors">
                    <TableCell className="text-xs font-mono max-w-[110px]"><TruncatedCell>{renderCell(item, "no_documento", item.no_documento)}</TruncatedCell></TableCell>
                    <TableCell className="text-xs text-center">{renderCell(item, "item_no", item.item_no)}</TableCell>
                    <TableCell className="text-xs font-mono max-w-[90px]"><TruncatedCell>{renderCell(item, "sku", item.sku)}</TruncatedCell></TableCell>
                    {showAuditCols && (
                      <TableCell className="text-xs font-mono max-w-[130px]">
                        <HsCodeSuggestion
                          currentCode={item.hs_code || ""}
                          compliance={complianceByKey[item.id]}
                          disabled={!!editingId || isEmbarqueLocked}
                          onAccept={(code) => handleInlineSave(item, "hs_code" as EditableField, code)}
                        />
                      </TableCell>
                    )}
                    <TableCell className="text-xs max-w-[200px]">
                      <div className="flex items-center gap-1">
                        <TruncatedCell>{renderCell(item, "descripcion", item.descripcion)}</TruncatedCell>
                        <BenefitAduaneroBadge value={(item as any).beneficio_aduanero} legacyAplicaTlc={item.aplica_tlc} />
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-right font-mono">
                      {renderInlineCell(item, "cantidad", Number(item.cantidad).toLocaleString())}
                    </TableCell>
                    <TableCell className="text-xs">{renderCell(item, "unidad_medida", item.unidad_medida)}</TableCell>
                    {(isPacking || isTransport) && (
                      <TableCell className="text-xs text-right font-mono">
                        {renderInlineCell(item, "peso_bruto_kg", Number(item.peso_bruto_kg || 0) > 0 ? Number(item.peso_bruto_kg).toLocaleString() : "—")}
                      </TableCell>
                    )}
                    {isFinancial && (
                      <TableCell className="text-xs text-right font-mono">
                        {renderInlineCell(item, "precio_unitario", Number(item.precio_unitario) > 0 ? `$${Number(item.precio_unitario).toLocaleString()}` : "—")}
                      </TableCell>
                    )}
                    {isFinancial && (
                      <TableCell className="text-xs text-right font-mono">
                        {renderCell(item, "sub_total", Number(item.sub_total) > 0 ? `$${Number(item.sub_total).toLocaleString()}` : "—")}
                      </TableCell>
                    )}
                      {showAuditCols && (
                        <TableCell className="text-xs text-right font-mono min-w-[120px]" onClick={(e) => e.stopPropagation()}>
                          {editingId === item.id ? (
                            <BenefitAduaneroSelect
                              itemId={item.id}
                              value={(item as any).beneficio_aduanero}
                              legacyAplicaTlc={item.aplica_tlc}
                              variant="compact"
                              className="w-full max-w-full [&_button]:w-full [&_button]:max-w-full [&_button]:min-w-0 [&_button>span]:truncate [&_button>span]:block [&_button>span]:text-left"
                            />
                          ) : (() => {
                            const hasCode = (item.hs_code ?? "").trim();
                            const compliance = complianceByItemId[item.id];
                            const ext = externalLandedCosts?.[item.id];
                            // Prioridad: RPC (fuente única de verdad) > lookup local.
                            const rpcArancel = ext?.arancelPctEfectivo;
                            if (rpcArancel == null && (!hasCode || !compliance)) return "—";
                            const originalGravamen = compliance?.gravamen ?? rpcArancel ?? 0;
                            const benefit = resolveBenefitValue((item as any).beneficio_aduanero, item.aplica_tlc);
                            const arancelEfectivo = rpcArancel != null
                              ? rpcArancel
                              : (benefit === "GENERAL" ? originalGravamen : 0);
                             return benefit !== "GENERAL" ? (
                               <span>
                                 <span className="line-through text-muted-foreground">{originalGravamen}%</span>{" "}
                                 <span className="text-green-600 dark:text-green-400 font-semibold">{arancelEfectivo}%</span>
                               </span>
                             ) : (
                               <span>{arancelEfectivo}%</span>
                             );
                          })()}
                        </TableCell>
                      )}
                     {showAuditCols && (() => {
                       const benefit = resolveBenefitValue((item as any).beneficio_aduanero, item.aplica_tlc);
                       const ivaZeroByBenefit = benefit === "LEY_1715" || benefit === "ZONA_FRANCA";
                       const compliance = complianceByItemId[item.id];
                       const hasCode = (item.hs_code ?? "").trim();
                       const baseIva = hasCode && compliance ? (item.iva_override != null ? Number(item.iva_override) : compliance.iva) : null;
                       const effectiveIva = ivaZeroByBenefit ? 0 : baseIva;
                       return (
                         <TableCell className="text-xs text-right font-mono">
                           {editingId === item.id ? (
                             ivaZeroByBenefit ? (
                               <span title="IVA exento por régimen aduanero">
                                 {baseIva != null && (
                                   <span className="line-through text-muted-foreground mr-1">{baseIva}%</span>
                                 )}
                                 <span className="text-green-600 dark:text-green-400 font-semibold">0%</span>
                               </span>
                             ) : (
                               <Input
                                 type="number"
                                 min={0}
                                 step={0.5}
                                 value={editValues["iva_override"] ?? ""}
                                 onChange={(e) => setEditValues(prev => ({ ...prev, iva_override: e.target.value }))}
                                 className="h-7 text-xs min-w-[60px]"
                               />
                             )
                           ) : (
                             effectiveIva == null ? "—" : (
                               ivaZeroByBenefit && baseIva != null && baseIva !== 0 ? (
                                 <span>
                                   <span className="line-through text-muted-foreground">{baseIva}%</span>{" "}
                                   <span className="text-green-600 dark:text-green-400 font-semibold">0%</span>
                                 </span>
                               ) : (
                                 <span>{effectiveIva}%</span>
                               )
                             )
                           )}
                         </TableCell>
                       );
                     })()}
                    {showAuditCols && (() => {
                      const itemIdx = items.indexOf(item);
                      const lc = landedCosts[itemIdx];
                      return (
                        <>
                          {!externalLandedCosts && (
                            <TableCell className="text-xs text-right font-mono font-semibold text-primary">
                              {lc && lc.landedCostUnitario > 0 ? `$${lc.landedCostUnitario.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}
                            </TableCell>
                          )}
                          <TableCell className="text-xs text-right font-mono font-medium text-emerald-700 dark:text-emerald-400">
                            {lc && lc.landedCostUnitarioCOP != null && lc.landedCostUnitarioCOP > 0 ? (
                              externalLandedCosts ? (
                                <HoverCard openDelay={100} closeDelay={200}>
                                  <HoverCardTrigger asChild>
                                    <span className="cursor-help underline decoration-dotted underline-offset-2">
                                      {copFmt.format(lc.landedCostUnitarioCOP)}
                                      {(() => {
                                        const ext = externalLandedCosts[item.id];
                                        const moneda = ext?.monedaOrigen;
                                        if (moneda && moneda !== "COP") {
                                          return <span className="block text-[10px] text-muted-foreground font-normal">orig: {moneda}</span>;
                                        }
                                        return null;
                                      })()}
                                    </span>
                                  </HoverCardTrigger>
                                  <HoverCardContent side="left" align="center" className="w-72 p-3 text-xs space-y-1.5 z-[9999]">
                                    {(() => {
                                      const ext = externalLandedCosts[item.id];
                                      if (!ext) return <p className="text-muted-foreground">Sin desglose disponible</p>;
                                      return (
                                        <>
                                          <p className="font-semibold text-foreground mb-2">Desglose de Prorrateo</p>
                                          <div className="flex justify-between"><span className="text-muted-foreground">FOB Item</span><span className="font-mono">{copFmt.format(ext.fobItemCOP ?? 0)}</span></div>
                                          <div className="flex justify-between"><span className="text-muted-foreground">+ Gastos por Peso (Fletes/Transporte)</span><span className="font-mono">{copFmt.format(ext.fleteAsignado ?? 0)}</span></div>
                                          <div className="flex justify-between"><span className="text-muted-foreground">+ Gastos por Valor (Seguro/Otros)</span><span className="font-mono">{copFmt.format(ext.seguroAsignado ?? 0)}</span></div>
                                          <div className="flex justify-between"><span className="text-muted-foreground">+ Arancel</span><span className="font-mono">{copFmt.format(ext.arancelItemCOP ?? 0)}</span></div>
                                          <div className="flex justify-between"><span className="text-muted-foreground">+ IVA Asignado</span><span className="font-mono">{copFmt.format(ext.ivaItemCOP ?? 0)}</span></div>
                                          <div className="border-t border-border pt-1.5 mt-1.5 flex justify-between font-semibold text-foreground">
                                            <span>= Landed Cost Total</span>
                                            <span className="font-mono">{copFmt.format(ext.landedCostTotalCOP ?? 0)}</span>
                                          </div>
                                          {ext.monedaOrigen && ext.monedaOrigen !== "COP" && (
                                            <p className="text-[10px] text-muted-foreground mt-1">
                                              Conversión desde {ext.monedaOrigen} × {(externalTrmAduana && externalTrmAduana > 0
                                                ? externalTrmAduana
                                                : trmMap[ext.monedaOrigen]
                                              )?.toLocaleString("es-CO") ?? "?"} COP
                                              {externalTrmAduana && externalTrmAduana > 0 && (
                                                <span className="ml-1 text-[9px] uppercase tracking-wide opacity-70">(TRM Aduana)</span>
                                              )}
                                            </p>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </HoverCardContent>
                                </HoverCard>
                              ) : (
                                copFmt.format(lc.landedCostUnitarioCOP)
                              )
                            ) : trm ? "—" : "TRM…"}
                          </TableCell>
                          {externalLandedCosts && (() => {
                            const ext = externalLandedCosts[item.id];
                            const qty = Number(item.cantidad) || 0;
                            const lcTotal = (ext?.landedCostUnitarioCOP ?? 0) * qty;
                            return (
                              <TableCell className="text-xs text-right font-mono font-semibold text-primary">
                                {lcTotal > 0 ? copFmt.format(lcTotal) : "—"}
                              </TableCell>
                            );
                          })()}
                        </>
                      );
                    })()}
                    {showAuditCols && (() => {
                      // ICO viene exclusivamente del RPC calculate_landed_cost_preview.
                      // No recalculamos client-side: la fórmula vive sólo en el motor SQL
                      // para evitar divergencia cuando se extienda a vehículos / cigarrillos.
                      const icoCop = externalLandedCosts?.[item.id]?.icoCOP ?? 0;
                      return (
                        <TableCell className="text-xs text-right font-mono">
                          {icoCop > 0 ? copFmt.format(icoCop) : "—"}
                        </TableCell>
                      );
                    })()}
                    {showAuditCols && (
                      <TableCell className="text-center">
                        {(() => {
                          const code = (item.hs_code ?? "").trim();

                          // ── State 1 (Fix R2): No HS code assigned ──
                          if (!code) {
                            return (
                              <TooltipProvider delayDuration={100}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <ShieldCheck
                                      className="h-4 w-4 text-muted-foreground/40 mx-auto cursor-pointer hover:text-muted-foreground transition-colors"
                                      onClick={() => setComplianceModal({ item })}
                                    />
                                  </TooltipTrigger>
                                  <TooltipContent side="left" className="text-xs z-50">
                                    Sin partida arancelaria asignada
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            );
                          }

                          const status = complianceStatusByKey[item.id];
                          const cr = complianceByKey[item.id];

                          // ── State 2: Lookup in flight ──
                          if (status === "loading" || (status === undefined && !cr)) {
                            return (
                              <Loader2 className="h-4 w-4 text-muted-foreground/50 mx-auto animate-spin" />
                            );
                          }

                          // ── State 3 (Fix R3): HS code exists but NO match in arancel_1881 ──
                          if (status === "unknown" || !cr) {
                            return (
                              <TooltipProvider delayDuration={100}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <AlertCircle
                                      className="h-4 w-4 text-amber-500 mx-auto cursor-pointer hover:text-amber-600 transition-colors"
                                      onClick={() => setComplianceModal({ item })}
                                    />
                                  </TooltipTrigger>
                                  <TooltipContent side="left" className="max-w-xs text-xs z-50">
                                    <p className="font-semibold text-amber-600 dark:text-amber-400">
                                      Código HS desconocido
                                    </p>
                                    <p className="text-muted-foreground">
                                      No se encontró en el arancel oficial. Verifique el código.
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            );
                          }

                          const regla = cr.regla;
                          const hasRegulatoryRule =
                            !!regla && (!!regla.entidad || !!regla.tipo_tramite || !!regla.nivel_riesgo);

                          // ── State 4 (Fix R2): Verified, no regulatory rule ──
                          if (!hasRegulatoryRule) {
                            return (
                              <TooltipProvider delayDuration={100}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <ShieldCheck
                                      className="h-4 w-4 text-emerald-600 dark:text-emerald-400 mx-auto cursor-pointer hover:text-emerald-700 transition-colors"
                                      onClick={() => setComplianceModal({ item })}
                                    />
                                  </TooltipTrigger>
                                  <TooltipContent side="left" className="max-w-xs text-xs z-50">
                                    <p className="font-semibold text-emerald-600 dark:text-emerald-400">
                                      Partida verificada
                                    </p>
                                    <p className="text-muted-foreground">
                                      Sin restricciones detectadas.
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            );
                          }

                          // ── State 5: Active rule (Fix R6 — render even if nivel_riesgo is empty) ──
                          const riesgo = (regla!.nivel_riesgo ?? "").toLowerCase();
                          const isCritico = riesgo === "crítico" || riesgo === "critico";
                          const isAltoMedio = riesgo === "alto" || riesgo === "medio";
                          const hasRiesgoDefinido = !!riesgo;
                          const badgeLabel = hasRiesgoDefinido
                            ? regla!.nivel_riesgo
                            : "Trámite";

                          return (
                            <TooltipProvider delayDuration={100}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge
                                    onClick={() => setComplianceModal({ item })}
                                    className={cn(
                                      "cursor-pointer text-[10px] px-1.5 py-0.5",
                                      isCritico && "bg-destructive text-destructive-foreground hover:bg-destructive/80",
                                      isAltoMedio && "bg-orange-500 text-white hover:bg-orange-600",
                                      !isCritico && !isAltoMedio && hasRiesgoDefinido && "bg-muted text-muted-foreground",
                                      !hasRiesgoDefinido && "bg-blue-500 text-white hover:bg-blue-600"
                                    )}
                                  >
                                    {badgeLabel}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent side="left" align="center" className="max-w-xs text-xs space-y-1 p-3 z-50">
                                  {regla!.entidad && <p><span className="font-semibold">Entidad:</span> {regla!.entidad}</p>}
                                  {regla!.tipo_tramite && <p><span className="font-semibold">Trámite:</span> {regla!.tipo_tramite}</p>}
                                  {regla!.descripcion_regla && <p><span className="font-semibold">Regla:</span> {regla!.descripcion_regla}</p>}
                                  {!hasRiesgoDefinido && (
                                    <p className="text-muted-foreground italic pt-1">
                                      Nivel de riesgo no definido — revisar manualmente.
                                    </p>
                                  )}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          );
                        })()}
                      </TableCell>
                    )}
                    {showAuditCols && (
                      <TableCell className="text-xs text-right font-mono" onClick={(e) => e.stopPropagation()}>
                        <EditableCell
                          value={item.precio_venta_esperado}
                          type="number"
                          disabled={isEmbarqueLocked}
                          displayFormat={(val) => {
                            const v = Number(val) || 0;
                            return v > 0 ? copFmt.format(v) : "—";
                          }}
                          onSave={async (raw) => {
                            const CONGELADO_OLD = String(item.precio_venta_esperado ?? "");
                            const cleaned = parseFloat(raw.replace(/[^0-9.\-]/g, "")) || 0;
                            const { error } = await supabase.from("base_inventario").update({ precio_venta_esperado: cleaned } as any).eq("id", item.id);
                            if (error) throw error;
                            await logChange(item.documento_id, "precio_venta_esperado", CONGELADO_OLD, String(cleaned));
                            queryClient.invalidateQueries({ queryKey: ["inventory"] });
                            toast({ title: "✅ Precio venta actualizado" });
                          }}
                        />
                      </TableCell>
                    )}
                    {showAuditCols && (() => {
                      const itemIdx = items.indexOf(item);
                      const lc = landedCosts[itemIdx];
                      const landedCostCOP = lc?.landedCostUnitarioCOP ?? 0;
                      const precioVenta = Number(item.precio_venta_esperado) || 0;
                      if (precioVenta <= 0 || landedCostCOP <= 0) {
                        return <TableCell className="text-xs text-center text-muted-foreground">—</TableCell>;
                      }
                      const margen = ((precioVenta - landedCostCOP) / precioVenta) * 100;
                      const isGreen = margen >= 30;
                      const isYellow = margen >= 15 && margen < 30;
                      return (
                        <TableCell className="text-xs text-center">
                          <Badge variant="outline" className={cn(
                            "text-[10px] font-mono border",
                            isGreen && "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-700",
                            isYellow && "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-700",
                            !isGreen && !isYellow && "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-700",
                          )}>
                            {margen >= 0 ? "+" : ""}{margen.toFixed(1)}%
                          </Badge>
                        </TableCell>
                      );
                    })()}
                    <TableCell className="text-xs max-w-[180px] text-muted-foreground"><TruncatedCell>{renderCell(item, "nota_item", item.nota_item)}</TruncatedCell></TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        {editingId === item.id ? (
                          <>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-green-500 hover:text-green-400" onClick={saveEdit}>
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={cancelEdit}>
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          <>
                            {onOpenTraceability && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-muted-foreground hover:text-primary"
                                title="Ver recibo de trazabilidad de costo"
                                onClick={() => onOpenTraceability(item.id)}
                              >
                                <Search className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={() => startEdit(item)} disabled={isEmbarqueLocked} title={isEmbarqueLocked ? "Embarque liquidado" : "Editar"}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => setDeleteId(item.id)} disabled={isEmbarqueLocked} title={isEmbarqueLocked ? "Embarque liquidado" : "Eliminar"}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  </React.Fragment>
                ))}
                {/* === Dynamic Totals Footer Row === */}
                {filtered.length > 0 && (
                  <TableRow className="border-t-2 border-border bg-muted/40 font-semibold">
                    <TableCell colSpan={3} className="text-xs text-muted-foreground">Totales</TableCell>
                    {showAuditCols && <TableCell />}
                    <TableCell />
                    <TableCell className="text-xs text-right font-mono">
                      {filtered.reduce((s, i) => s + (parseFloat(String(i.cantidad)) || 0), 0).toLocaleString()}
                    </TableCell>
                    <TableCell />
                    {(isPacking || isTransport) && (
                      <TableCell className="text-xs text-right font-mono font-semibold">
                        {filtered.reduce((s, i) => s + (parseFloat(String(i.peso_bruto_kg)) || 0), 0).toLocaleString("es-CO", { maximumFractionDigits: 1 })} kg
                      </TableCell>
                    )}
                    {isFinancial && (
                      <>
                        <TableCell className="text-xs text-right font-mono text-muted-foreground">—</TableCell>
                        <TableCell className="text-xs text-right font-mono font-semibold">
                          ${filtered.reduce((s, i) => s + (parseFloat(String(i.sub_total)) || 0), 0).toLocaleString()}
                        </TableCell>
                      </>
                    )}
                    {showAuditCols && (
                      <>
                        <TableCell className="text-xs text-right text-muted-foreground">—</TableCell>
                        <TableCell className="text-xs text-right text-muted-foreground">—</TableCell>
                        {!externalLandedCosts && <TableCell className="text-xs text-right text-muted-foreground">—</TableCell>}
                        <TableCell className="text-xs text-right text-muted-foreground">—</TableCell>
                        {externalLandedCosts && (
                          <TableCell className="text-xs text-right font-mono font-semibold text-primary">
                            {copFmt.format(filtered.reduce((s, i) => {
                              const ext = externalLandedCosts[i.id];
                              const qty = Number(i.cantidad) || 0;
                              return s + ((ext?.landedCostUnitarioCOP ?? 0) * qty);
                            }, 0))}
                          </TableCell>
                        )}
                        <TableCell />
                      </>
                    )}
                    <TableCell />
                    <TableCell />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
      <TablePagination
        currentPage={currentPage}
        totalItems={filtered.length}
        rowsPerPage={rowsPerPage}
        onPageChange={setCurrentPage}
        onRowsPerPageChange={handleRowsPerPageChange}
      />

      <AlertDialog open={!!deleteId} onOpenChange={(open) => { if (!open) setDeleteId(null); }}>
        <AlertDialogContent className="glass border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar ítem de inventario?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará el ítem seleccionado. El consumo mensual no se verá afectado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {complianceModal && (
        <ComplianceModal
          open={!!complianceModal}
          onOpenChange={(open) => { if (!open) setComplianceModal(null); }}
          codigoPartida={complianceModal.item.hs_code || ""}
          descripcionProducto={complianceModal.item.descripcion || ""}
          compliance={complianceByKey[complianceModal.item.id]}
          documentoId={complianceModal.item.documento_id}
        />
      )}
    </div>
  );
}
